import { Trophy, Handshake } from "@/lib/icons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { HotSeatState } from "./useHotSeatMatch";

/**
 * End-of-match dialog. Three exits: rematch with same players, change
 * players (back to setup), or quit (caller-defined link target).
 */
export default function MatchResultDialog({
  state,
  open,
  onRematch,
  onChangePlayers,
  onExit,
  detail,
}: {
  state: HotSeatState;
  open: boolean;
  onRematch: () => void;
  onChangePlayers: () => void;
  onExit: () => void;
  /** Optional one-line summary, e.g. "Defeated by timeout" or "Round 3 win". */
  detail?: string;
}) {
  const winner = state.winner === null ? null : state.players[state.winner];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onExit()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {winner ? (
              <>
                <Trophy size={18} className="text-primary" aria-hidden="true" />
                {winner.name} wins!
              </>
            ) : (
              <>
                <Handshake size={18} className="text-primary" aria-hidden="true" />
                It's a draw
              </>
            )}
          </DialogTitle>
          {detail && <DialogDescription>{detail}</DialogDescription>}
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 py-2">
          {state.players.map((p) => (
            <div
              key={p.id}
              className={`rounded-md border p-2.5 text-center ${
                state.winner === p.id
                  ? "border-primary bg-primary/5"
                  : "border-border"
              }`}
            >
              <div className="text-xs text-muted-foreground truncate">{p.name}</div>
              <div className="text-lg font-bold font-mono tabular-nums text-foreground">
                {state.stats[p.id]}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onExit} className="sm:order-1">
            Quit
          </Button>
          <Button variant="outline" onClick={onChangePlayers} className="sm:order-2">
            Change players
          </Button>
          <Button onClick={onRematch} className="sm:order-3">
            Rematch
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}